import { Pic } from "./Pic";

import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Store, Sun, Waves } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";

const PHOTOS = ["/img/emma/balneare-0.jpg", "/img/emma/balneare-1.jpg", "/img/emma/balneare-2.jpg", "/img/emma/balneare-3.jpg", "/img/emma/balneare-4.jpg", "/img/emma/balneare-5.jpg"];

const FEATURES = [
  ["f0", Store, "Strutture complete", "Bar, ristoranti e lounge sulla spiaggia, chioschi, cabine, docce e spogliatoi: tutto lo stabilimento progettato e prodotto da un unico partner."],
  ["f1", Sun, "Fisse tutto l'anno", "Strutture stabili e durature, pensate per restare in spiaggia in ogni stagione e lavorare anche oltre l'estate."],
  ["f2", Waves, "Legno trattato per il mare", "Legno lamellare trattato per resistere a sole, salsedine e sabbia, con finiture pensate per l'ambiente marino."],
];

const askBeach = (t) => {
  const message = t("beach.msg", "Vorrei un preventivo per uno stabilimento balneare prefabbricato in legno.");
  window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: "Stabilimento balneare", message } }));
  scrollToId("#contatti");
};

const Photo = ({ src, i, t }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`group overflow-hidden ${i === 0 ? "col-span-2 aspect-[2/1] lg:row-span-2 lg:aspect-auto" : i === PHOTOS.length - 1 ? "col-span-2 aspect-[2/1] lg:col-span-1 lg:aspect-[4/3]" : "aspect-[4/3]"}`}
    data-testid={`beach-photo-${i}`}
  >
    <Pic src={src} alt={t("beach.alt", "Stabilimento balneare prefabbricato in legno realizzato da Emma Industrie")} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
  </motion.div>
);

export const Beach = ({ full = false }) => {
  const t = useT();
  return (
    <section id="balneari" className="keep-dark relative bg-[#0E2A33] px-5 py-28 text-[#F3EFE6] sm:px-8 lg:py-40" data-testid="beach-section">
      <div className="mx-auto max-w-[1500px]">
        <SectionLabel index="≈">{t("beach.label", "Stabilimenti balneari")}</SectionLabel>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <MaskLines
            as={full ? "h1" : "h2"}
            className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-[4.4rem]"
            lines={[t("beach.h1", "Il tuo stabilimento"), <span key="b">{t("beach.h2a", "balneare,")} <em className="font-serif font-normal text-[#E8C79A]">{t("beach.h2em", "in legno.")}</em></span>]}
          />
          <Reveal delay={0.2} className="max-w-md text-base leading-relaxed text-[#D5DEDF] sm:text-lg lg:col-span-5">
            {t("beach.intro", "Progettiamo e produciamo stabilimenti balneari prefabbricati in legno: strutture complete, fisse tutto l'anno, che portano il calore del legno sulla spiaggia e resistono all'ambiente marino.")}
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-2 gap-3 lg:grid-cols-3" data-testid="beach-gallery">
          {PHOTOS.map((src, i) => <Photo key={src} src={src} i={i} t={t} />)}
        </div>
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {FEATURES.map(([id, Icon, title, text], i) => (
            <Reveal key={id} delay={i * 0.1} className="border-t border-white/15 pt-6" data-testid={`beach-feature-${id}`}>
              <Icon className="h-6 w-6 text-[#E8C79A]" />
              <h3 className="mt-4 font-display text-xl font-bold">{t(`beach.${id}.t`, title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#D5DEDF] sm:text-base">{t(`beach.${id}.d`, text)}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-16 flex flex-col gap-4 sm:flex-row sm:items-center">
          <button onClick={() => askBeach(t)} className="inline-flex items-center justify-center gap-3 rounded-full bg-[#E8C79A] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#0E2A33] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#F3DDB9]" data-testid="beach-cta">
            {t("beach.cta", "Richiedi un preventivo per il tuo lido")} <ArrowRight className="h-4 w-4" />
          </button>
          {!full && (
            <Link to="/stabilimenti-balneari" className="inline-flex items-center justify-center gap-2 px-4 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F3EFE6] underline-offset-4 transition-colors duration-300 hover:text-[#E8C79A] hover:underline" data-testid="beach-page-link">
              {t("beach.page", "Pagina dedicata ai lidi")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
};
